<template>
  <div
    class="h-full w-full flex flex-col bg-white dark:bg-gray-900"
  >
      <client-only>
        <incidents />

        <!-- App download prompt banner -->
        <banner />

        <div
          v-if="active && accounts"
          class="box-border text-base font-normal flex relative h-full overflow-hidden max-sm:!bg-white max-sm:dark:!bg-gray-900"
          :class="[
            $auth.theme.light.gradient && $colorMode.value === 'light' ? $auth.theme.light.gradient : `bg-primary-${$auth.theme.light.shade}`,
            $auth.theme.dark.gradient ? $auth.theme.dark.gradient : `dark:bg-neutral-950`,
          ]"
        >
          <!-- Temp grants display -->

          <div v-if="grants" class="hidden fixed top-0 right-0 m-2 px-3 py-2 rounded-lg text-sm bg-gray-700 shadow text-white z-80000 group cursor-pointer">
            Grants required

            <div class="hidden group-hover:block p-3 bg-white border absolute text-gray-800 shadow rounded-lg top-0 left-1/2 translate-y-full -translate-x-1/2 w-52">
              <ul>
                <li v-for="(actions, grant) in grants" :key="grant">
                  <span class="capitalize font-semibold">{{ grant }}</span> - {{ actions.join(', ') }}
                </li>
              </ul>
            </div>
          </div>

          <!-- Resizable nav region. Width is driven by either `uiStore.menu`
               (legacy collapse toggle — forces icon-rail width) or
               `uiStore.navWidth` (user drag). The drag handle is hidden
               while collapsed since resizing the icon rail makes no sense. -->
          <div
            class="hidden sm:flex relative h-full flex-shrink-0 transition-[width] duration-200 ease-out"
            :style="{ width: `${effectiveNavWidth}px` }"
          >
            <PrimaryMenu class="w-full" />

            <!-- Drag handle: a thin invisible column on the right edge that
                 becomes col-resize on hover and highlights during drag. -->
            <div
              v-if="!uiStore.menu"
              role="separator"
              :aria-label="$t('general.resize')"
              aria-orientation="vertical"
              class="absolute top-0 right-0 h-full w-1 cursor-col-resize group"
              @mousedown="startResize"
            >
              <div
                class="absolute inset-y-0 right-0 w-px bg-transparent group-hover:bg-primary-400 dark:group-hover:bg-primary-600 transition-colors"
                :class="{ '!bg-primary-500': isResizing }"
              />
            </div>
          </div>

          <transition name="mobile-nav">
            <div
              v-if="isMobileMenuOpen"
              class="fixed inset-y-0 left-0 z-80000 w-72 max-w-[calc(100vw-3rem)] sm:hidden"
              :class="shellBackgroundClasses"
            >
              <PrimaryMenu />
            </div>
          </transition>

          <!-- Right -->
          <div class="flex max-w-full flex-col grow transition-all duration-500 ease-in-out overflow-hidden relative z-30 p-0 sm:p-1.5 sm:pl-0">
            <!-- Content -->
            <div class="grow h-100 shrink overflow-hidden border-0 bg-gray-50 shadow-none dark:border-gray-800 dark:bg-gray-900/50 sm:rounded-lg sm:border sm:border-gray-200 sm:shadow-lg">
              <div v-if="activeSettingsMode" class="h-full min-h-0 flex bg-white dark:bg-gray-900">
                <SettingsMenu
                  :groups="activeSettingsConfig.groups"
                  :title="activeSettingsConfig.label"
                  :collapsed="uiStore.settingsMenuCollapsed"
                  collapsible
                  class="hidden lg:flex"
                  @toggle-collapsed="uiStore.toggleSettingsMenu()"
                />
                <div class="min-w-0 grow h-full overflow-hidden">
                  <slot />
                </div>
              </div>
              <slot v-else />
            </div>

            <LayoutMobile />
          </div>

          <transition name="settings-nav">
            <div
              v-if="activeSettingsMode && settingsMenuOpen"
              class="fixed inset-y-0 left-0 z-80000 w-80 max-w-[calc(100vw-3rem)] lg:hidden"
            >
              <SettingsMenu
                :groups="activeSettingsConfig.groups"
                :title="activeSettingsConfig.label"
                class="!w-full"
              />
            </div>
          </transition>

          <search />

          <update />

          <lock />

          <holiday-animation />

          <!--<messages />-->

          <panel-host />
        </div>

        <div v-if="admin" class="h-12 w-full bg-orange-600 dark:bg-orange-950 text-white border-t px-3 border-orange-500 dark:border-orange-900 flex items-center justify-between">
          <div class="flex items-center">
            <o-icon icon="warning" size="12" class="text-white mr-2" />
            <p class="text-sm">
              You are currently signed in as an admin
            </p>
          </div>

          <a v-if="$auth.info" :href="`https://admin.ovatu.com/location/${$auth.info.location.idLocation}`" target="_blank" class="text-sm">
            Open admin panel
          </a>
        </div>

        <div v-if="dev" class="hidden h-12 w-full bg-sky-600 dark:bg-sky-950 text-white border-t px-3 border-sky-700 dark:border-sky-900 flex items-center justify-center">
          <o-icon icon="info" size="12" class="text-white mr-2" />
          <p class="text-sm">
            You are currently viewing a development version
          </p>
        </div>

        <maintenance />
      </client-only>

    <transition name="fade">
      <div v-if="isMobileMenuOpen" class="fixed inset-0 z-70000 h-full w-full bg-black/25 sm:hidden" @click="uiStore.setMobile(false)" />
    </transition>
    <transition name="fade">
      <div v-if="settingsMenuOpen" class="fixed inset-0 z-70000 h-full w-full bg-black/25 lg:hidden" @click="settingsMenuOpen = false" />
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useUIStore } from '~/stores/useUIStore'
import { useAuthStore } from '~/stores/useAuthStore'
import { useReactiveTheme } from '~/composables/useTheme'
import { settingsNavigationItemMatchesPath } from '@/lib/settingsSearch'

// @ts-ignore
import PrimaryMenu from '@/components/layout/PrimaryMenu.vue'
import SettingsMenu from '@/components/layout/SettingsMenu.vue'
// @ts-ignore
import Search from '@/components/search/Search.vue'
// @ts-ignore
import Update from '@/components/layout/Update.vue'
// @ts-ignore
import Incidents from '@/components/layout/Incidents.vue'
// @ts-ignore
import LayoutMobile from '@/components/layout/Mobile.vue'
// @ts-ignore
import Banner from '@/components/layout/Banner.vue'
// @ts-ignore
import HolidayAnimation from '@/components/layout/HolidayAnimation.vue'
// @ts-ignore
import Maintenance from '@/components/layout/Maintenance.vue'
// @ts-ignore
import Lock from '@/components/auth/elements/Lock.vue'
// @ts-ignore
import PanelHost from '@/components/panel/PanelHost.vue'

import { groups as manageGroups } from '@/components/manage/pages'
import { groups as minisiteGroups } from '@/components/minisite/pages'
import { groups as accountGroups } from '@/components/account/pages'

const { $auth, $colorMode, $notification, $redirect, $panel } = useNuxtApp()
const runtimeConfig = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const uiStore = useUIStore()
const authStore = useAuthStore()

useReactiveTheme(
  () => ($auth as any).theme,
  () => ($auth as any).info?.location?.title,
)

const active = computed(() => authStore.active)
const accounts = computed(() => authStore.accounts)
const isMobileMenuOpen = computed(() => uiStore.mobile)
const settingsMenuOpen = useState<boolean>('settings-menu-open', () => false)
const shellBackgroundClasses = computed(() => [
  ($auth as any).theme.light.gradient && $colorMode.value === 'light' ? ($auth as any).theme.light.gradient : `bg-primary-${($auth as any).theme.light.shade}`,
  ($auth as any).theme.dark.gradient ? ($auth as any).theme.dark.gradient : 'dark:bg-neutral-950',
])

const grants = computed(() => {
  return route.meta?.grants
})

const dev = computed(() => runtimeConfig.public.instance === 'dev')
const admin = computed(() => ($auth as any)?.info?.adminUser)

type SettingsMode = 'manage' | 'minisite' | 'account'

interface SettingsGroup {
  label: string
  icon?: string
  items: any[]
}

// Settings nav configs, keyed by route family. The page files still live in
// their legacy folders, but Nuxt aliases expose these as /manage, /minisite,
// and /account.
const SETTINGS_CONFIGS: Record<SettingsMode, { label: string; groups: SettingsGroup[] }> = {
  manage: {
    label: 'general.manage',
    groups: manageGroups as SettingsGroup[],
  },
  minisite: {
    label: 'general.minisite',
    groups: minisiteGroups as SettingsGroup[],
  },
  account: {
    label: 'general.account',
    groups: accountGroups as SettingsGroup[],
  },
}

// Resolve which settings mode owns a given path by walking each config's items
// (and their children). `null` means the route isn't in any settings sidebar.
const modeForPath = (path: string): SettingsMode | null => {
  for (const mode of ['manage', 'minisite', 'account'] as const) {
    for (const group of SETTINGS_CONFIGS[mode].groups) {
      for (const item of group.items as any[]) {
        if (settingsNavigationItemMatchesPath(item, path)) return mode
        if (item.children?.some((child: any) => settingsNavigationItemMatchesPath(child, path))) return mode
      }
    }
  }
  return null
}

const activeSettingsMode = computed(() => modeForPath(route.fullPath.replace(/#.*$/, '')))
const activeSettingsConfig = computed(() => {
  const mode = activeSettingsMode.value
  return mode ? SETTINGS_CONFIGS[mode] : SETTINGS_CONFIGS.manage
})

// Width of the nav column. When the user has the legacy collapse toggle on
// (`uiStore.menu === true`), we slam it to the icon-rail width and disable
// the resize handle — the user's drag-set width is preserved in the store
// and restored when they uncollapse.
const NAV_COLLAPSED_WIDTH = 64
const effectiveNavWidth = computed(() => uiStore.menu ? NAV_COLLAPSED_WIDTH : uiStore.navWidth)

// ---- Nav resize drag handler -----------------------------------------------
//
// The handle div listens for mousedown; we then bind document-level mousemove
// and mouseup so the drag continues even when the cursor leaves the 1px-wide
// handle. We also flip a CSS class on body that disables text selection and
// forces the col-resize cursor everywhere while dragging.

const isResizing = ref(false)
let startX = 0
let startWidth = 0

const startResize = (e: MouseEvent) => {
  if (uiStore.isMobile) return
  e.preventDefault()
  isResizing.value = true
  startX = e.clientX
  startWidth = uiStore.navWidth
  document.body.classList.add('nav-resizing')
  document.addEventListener('mousemove', onResizeMove)
  document.addEventListener('mouseup', stopResize)
}

const onResizeMove = (e: MouseEvent) => {
  uiStore.setNavWidth(startWidth + (e.clientX - startX))
}

const stopResize = () => {
  isResizing.value = false
  document.body.classList.remove('nav-resizing')
  document.removeEventListener('mousemove', onResizeMove)
  document.removeEventListener('mouseup', stopResize)
}

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', onResizeMove)
  document.removeEventListener('mouseup', stopResize)
})

watch(() => route.fullPath, () => {
  settingsMenuOpen.value = false
})

const showNotifications = () => {
  const notifications = ($auth as any).info?.panels || []

  for (const notification of notifications) {
    const options: Record<string, any> = {
      title: notification.title,
      text: notification.content,
      variant: notification.type,
      duration: 10000,
      width: 'w-200'
    }

    if (notification.action) {
      if (notification.action.internalUrl) {
        options.action = {
          click: () => {
            const match = ($redirect as any)?.match(notification.action.internalUrl)
            if (match.component === 'a') {
              window.location.href = match.path
            } else if (match.component === 'o-panel-link') {
              $panel.create(match.path)
            } else {
              router.push(match.path)
            }
          },
          label: notification.action.title
        }
      } else if (notification.action.url) {
        options.action = {
          click: () => { window.location.href = notification.action.url },
          label: notification.action.title
        }
      }
    }

    ($notification as any).toast(options)
  }
}

onMounted(() => {
  uiStore.setWidth(window.innerWidth)

  window.addEventListener('resize', () => {
    uiStore.setWidth(window.innerWidth)
  })

  showNotifications()
})
</script>

<style scoped>
/* Slide-swap between main nav and manage nav. `mode="out-in"` means one
   plays at a time; we use a short slide + fade so the transition reads as
   "this region's role changed" rather than two separate widgets. */
.nav-swap-enter-active,
.nav-swap-leave-active {
  transition: transform 220ms ease, opacity 180ms ease;
}
.nav-swap-enter-from {
  transform: translateX(-16px);
  opacity: 0;
}
.nav-swap-leave-to {
  transform: translateX(-16px);
  opacity: 0;
}

.mobile-nav-enter-active,
.mobile-nav-leave-active {
  transition: transform 180ms ease, opacity 160ms ease;
}

.mobile-nav-enter-from,
.mobile-nav-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

.settings-nav-enter-active,
.settings-nav-leave-active {
  transition: transform 180ms ease, opacity 160ms ease;
}

.settings-nav-enter-from,
.settings-nav-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}
</style>

<style>
/* Applied to <body> while the nav drag handle is active. Forces the
   col-resize cursor everywhere (so the cursor doesn't snap back when it
   crosses iframes / panels) and suppresses text selection. */
body.nav-resizing {
  cursor: col-resize !important;
  user-select: none;
}
body.nav-resizing * {
  cursor: col-resize !important;
}
</style>
